import { defineStore } from 'pinia';

/** A user-defined date range period (shape comes from the DateRange picker component). */
interface OwnDateRangePeriod {
  /** Unique identifier/value of the period (used for de-duplication) */
  value: any;
  /** Marks the period as user-created (set by addOwnDateRange) */
  isOwn?: boolean;
  /** Additional period fields (label, dates, ...) defined by the picker */
  [key: string]: any;
}

interface DateRangeState {
  /** The last (max 3) user-defined date ranges, newest first */
  lastOwnDateRanges: OwnDateRangePeriod[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/dateRange.ts` (namespace
 * `dateRange`). Persisted to `localStorage` (full state) — matches the legacy `vuexLocal`
 * reducer's `dateRange: state.dateRange` entry in `src/store/index.ts`.
 *
 * The legacy getter `lastOwnDateRanges` was a pure pass-through of the state of the same
 * name. In Pinia a getter cannot share a name with a state property, so it is dropped and
 * consumers read the state directly (`store.lastOwnDateRanges`).
 */
export const useDateRangeStore = defineStore('dateRange', {
  state: (): DateRangeState => ({
    lastOwnDateRanges: [],
  }),
  actions: {
    /**
     * Converted from legacy mutation `addOwnDateRange` (no matching legacy action existed).
     * Adds a user-defined date range to the front of the list, de-duplicating by `value` and
     * capping the list at the 3 most recent entries.
     */
    addOwnDateRange(period: OwnDateRangePeriod) {
      let periods = [...this.lastOwnDateRanges];

      periods = periods.filter((item) => item.value !== period.value);

      period.isOwn = true;

      periods.unshift(period);

      this.lastOwnDateRanges = periods.splice(0, 3);
    },
  },
  vuexPersist: {
    local: true,
  },
});
